import { InjectionToken, Provider } from '@angular/core';
import { Team } from '../models/tile-models';
import { InitialFixtureRule } from '../utils/fixture-selection';

/**
 * Per-tenant data of the prediction game that is configuration, not logic.
 *
 * Everything in here differs between clients (1. FC Köln, Bayer 04, …) while the
 * mapping code around it stays identical — so the apps layer supplies the values
 * and the data-access layer only consumes them.
 */
export interface PredictionGameTenantConfig {
  /** The tenant's own club — `team` in every fixture; the opponent comes from the backend. */
  team: Team;
  /** Fallback max points for "no goal" until the backend sends maxScoreFirstGoalScorerNoGoal. */
  noGoalPointsFallback: number;
  /**
   * Backend team ids whose logos are served from local assets instead of the backend URL
   * (workaround for missing logo assets in dev/test data). Empty = no overrides.
   */
  localTeamLogoIds: readonly string[];
  /**
   * Which fixture is auto-selected in the match paginator when there is no (or no matching)
   * `heimspielId` from `MATCH_CONTEXT` (a matching `heimspielId` always wins regardless of
   * this setting):
   * - `'next-tippable'` (effzeh): the next editable, not-yet-finished fixture wins — the
   *   backend's dev data flags even FINISHED fixtures as editable, so the flag alone is not
   *   trustworthy. Prefers the earliest kickoff at/after now; if every tippable kickoff has
   *   already passed (stale data), the one closest to now (latest) wins; if nothing is
   *   tippable at all (season over), falls back to the fixture with the latest kickoff.
   * - `'played-until-next-day-end'` (b04): a played fixture stays "current" until the end of
   *   the calendar day AFTER its kickoff (local time) — e.g. a Saturday 15:30 kickoff stays
   *   selected through Sunday 23:59:59.999, and only from Monday does the next upcoming
   *   fixture take over. Once every fixture's window has passed (season over), falls back to
   *   the fixture with the latest kickoff.
   *
   * See `findInitialFixtureIndex` in `@kikkz/shared/prediction-game/util`.
   */
  initialFixtureRule: InitialFixtureRule;
  /**
   * Only relevant for `initialFixtureRule: 'played-until-next-day-end'`, and only applied
   * under `isDevMode()`: shortens the "stays current" selection window from the default
   * (end of the calendar day after kickoff) to this many minutes after kickoff, so the
   * match paginator moves on quickly while manually testing against generated dev data.
   * Ignored in production builds and by the `'next-tippable'` rule. `undefined` = no
   * shortcut (the production window always applies, dev or not).
   */
  devSelectionGraceMinutes?: number;
  /**
   * Number of upcoming (PLANNED) fixtures shown in the "Spieltage" list of the user-stats
   * modal, on top of everything already played/live — `null` shows every remaining fixture
   * of the season (effzeh), a number caps the preview (b04: `5`, matching its long-standing
   * behavior). POSTPONED and CANCELLED fixtures are never shown, regardless of this setting.
   */
  upcomingFixturesShown: number | null;
  /**
   * Target of the "Teilnahmebedingungen und Datenschutzhinweisen" link the registration
   * screens show next to the consent checkbox — the club page describing the terms of THIS
   * prediction game, which is why it is configuration of the game and not of the host app.
   *
   * It lives here, and only here: the same URL used to be hard-wired into every template
   * that links it. `undefined` = the tenant has no in-app registration and shows no such
   * link (b04, whose users arrive already authenticated through the club's OAuth).
   */
  termsAndPrivacyUrl?: string;
}

export const PREDICTION_GAME_TENANT_CONFIG = new InjectionToken<PredictionGameTenantConfig>(
  'PREDICTION_GAME_TENANT_CONFIG',
);

/** Provides the tenant's prediction-game configuration to the data-access layer. */
export function providePredictionGameTenantConfig(config: PredictionGameTenantConfig): Provider {
  return { provide: PREDICTION_GAME_TENANT_CONFIG, useValue: config };
}
